import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Room, RoomEvent, Track, createLocalTracks, ConnectionQuality } from 'livekit-client';
import {
  Loader2, Phone, PhoneOff, Mic, MicOff, Video, VideoOff,
  AlertTriangle, Clock, Coins, SignalHigh, SignalMedium, SignalLow, Hourglass, Volume2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import Avatar from '../components/Avatar';
import { useAuth } from '../context/AuthContext';
import { getCall, cancelCall, endCall, getCallToken } from '../api/o2oCalls';
import { startRingback } from '../lib/sounds';

const fmtClock = (secs) => {
  const s = Math.max(Math.floor(secs), 0);
  const m = Math.floor(s / 60);
  return `${String(m).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
};

const QualityIcon = ({ quality }) => {
  if (quality === 'excellent') return <SignalHigh className="h-4 w-4 text-emerald-400" />;
  if (quality === 'good') return <SignalMedium className="h-4 w-4 text-amber-400" />;
  return <SignalLow className="h-4 w-4 text-red-400" />;
};

const StatChip = ({ icon: Icon, label, value, warn, testId }) => (
  <div
    className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold tabular-nums backdrop-blur ${
      warn ? 'bg-red-600/80 text-white' : 'bg-black/50 text-white'
    }`}
    data-testid={testId}
  >
    <Icon className="h-3.5 w-3.5" />
    <span className="opacity-70">{label}</span>
    <span>{value}</span>
  </div>
);

/**
 * Phase 54 · Live pay-per-minute call room.
 * Handles: RINGING (caller waiting) → ACTIVE (LiveKit) → terminal states.
 */
export default function O2OCallPage() {
  const { callId } = useParams();
  const navigate = useNavigate();
  const { user: me } = useAuth();

  const [call, setCall] = useState(null);
  const [error, setError] = useState(null);
  const [connState, setConnState] = useState('idle'); // idle | connecting | connected
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [quality, setQuality] = useState('excellent');
  const [tick, setTick] = useState(0);
  // Browser autoplay policy: remote audio may be blocked until a user gesture.
  const [audioBlocked, setAudioBlocked] = useState(false);

  const roomRef = useRef(null);
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const localTracksRef = useRef({ audio: null, video: null });
  const autoEndFired = useRef(false);
  const answeredAtRef = useRef(null);
  const serverSyncRef = useRef({ elapsed: 0, at: Date.now() });

  const iAmCreator = me && call && me.id === call.creator_id;
  const other = call ? (iAmCreator ? call.caller : call.creator) : null;

  // ── Poll server state ────────────────────────────────────────────────
  const refresh = useCallback(async () => {
    try {
      const c = await getCall(callId);
      setCall(c);
      if (c.answered_at) answeredAtRef.current = new Date(c.answered_at).getTime();
      if (typeof c.elapsed_seconds === 'number') {
        serverSyncRef.current = { elapsed: c.elapsed_seconds, at: Date.now() };
      }
      return c;
    } catch (e) {
      if (e.response?.status === 404) setError('Chiamata non trovata.');
      return null;
    }
  }, [callId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!call || error) return undefined;
    const interval = call.state === 'RINGING' ? 2000 : 5000;
    if (!['RINGING', 'ACTIVE'].includes(call.state)) return undefined;
    const t = setInterval(refresh, interval);
    return () => clearInterval(t);
  }, [call?.state, refresh, error]); // eslint-disable-line react-hooks/exhaustive-deps

  // 1s UI tick for timer/credits.
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 1000);
    return () => clearInterval(t);
  }, []);

  // Ringback tone while the caller waits for an answer.
  useEffect(() => {
    if (!call || call.state !== 'RINGING' || iAmCreator) return undefined;
    console.info('[o2o] ringback start');
    const stop = startRingback();
    return () => { stop(); console.info('[o2o] ringback stop'); };
  }, [call?.state, iAmCreator]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Resume blocked remote audio (must run inside a user gesture). */
  const enableAudio = useCallback(async () => {
    const room = roomRef.current;
    if (!room) return;
    try {
      await room.startAudio();
      setAudioBlocked(!room.canPlaybackAudio);
    } catch (e) {
      console.warn('[call] startAudio failed', e);
    }
  }, []);

  // Any tap/click while blocked → try to unlock audio silently.
  useEffect(() => {
    if (!audioBlocked) return undefined;
    const handler = () => { enableAudio(); };
    document.addEventListener('pointerdown', handler, { capture: true });
    document.addEventListener('keydown', handler, { capture: true });
    return () => {
      document.removeEventListener('pointerdown', handler, { capture: true });
      document.removeEventListener('keydown', handler, { capture: true });
    };
  }, [audioBlocked, enableAudio]);

  // ── LiveKit connection when ACTIVE ───────────────────────────────────
  useEffect(() => {
    if (!call || call.state !== 'ACTIVE' || connState !== 'idle') return;
    let cancelled = false;
    setConnState('connecting');

    const attachRemote = (track) => {
      try {
        if (track.kind === Track.Kind.Video) track.attach(remoteVideoRef.current || undefined);
        if (track.kind === Track.Kind.Audio) track.attach(remoteAudioRef.current || undefined);
      } catch (e) { console.warn('[call] attach', e); }
    };

    (async () => {
      try {
        const bundle = await getCallToken(callId);
        if (cancelled) return;
        const room = new Room({ adaptiveStream: true, dynacast: true });
        roomRef.current = room;
        room.on(RoomEvent.TrackSubscribed, attachRemote);
        room.on(RoomEvent.AudioPlaybackStatusChanged, () => {
          if (cancelled) return;
          console.info('[call] audio playback allowed:', room.canPlaybackAudio);
          setAudioBlocked(!room.canPlaybackAudio);
        });
        room.on(RoomEvent.ConnectionQualityChanged, (q, participant) => {
          if (participant?.isLocal) {
            if (q === ConnectionQuality.Excellent) setQuality('excellent');
            else if (q === ConnectionQuality.Good) setQuality('good');
            else setQuality('poor');
          }
        });
        await room.connect(bundle.livekit_url, bundle.livekit_token);
        if (cancelled) { room.disconnect().catch(() => {}); return; }
        const isVideo = (call.mode || 'video') === 'video';
        const tracks = await createLocalTracks({ audio: true, video: isVideo });
        for (const t of tracks) {
          await room.localParticipant.publishTrack(t);
          if (t.kind === Track.Kind.Video && localVideoRef.current) {
            t.attach(localVideoRef.current);
            localTracksRef.current.video = t;
          }
          if (t.kind === Track.Kind.Audio) localTracksRef.current.audio = t;
        }
        room.remoteParticipants.forEach((p) => {
          p.trackPublications.forEach((pub) => { if (pub.track) attachRemote(pub.track); });
        });
        setCamOn(isVideo);
        setConnState('connected');
        try { await room.startAudio(); } catch (_e) { /* blocked → banner */ }
        setAudioBlocked(!room.canPlaybackAudio);
      } catch (e) {
        console.error('[call] connect failed:', e);
        if (!cancelled) {
          // Technical failure → close the call server-side so no credits
          // keep being consumed (0-billing if within the first 30s).
          try { await endCall(callId, 'connection_failed'); } catch (_e) { /* ignore */ }
          setError('Connessione non riuscita. La chiamata è stata chiusa senza addebiti.');
        }
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call?.state]);

  // Cleanup on unmount.
  useEffect(() => () => {
    try {
      Object.values(localTracksRef.current).forEach((t) => { if (t) t.stop(); });
    } catch (_) { /* ignore */ }
    const room = roomRef.current;
    if (room && room.state !== 'disconnected') room.disconnect().catch(() => {});
  }, []);

  // Disconnect when the call turns terminal server-side.
  useEffect(() => {
    if (!call) return;
    if (['COMPLETED', 'REJECTED', 'TIMEOUT', 'CANCELLED'].includes(call.state)) {
      const room = roomRef.current;
      if (room && room.state !== 'disconnected') room.disconnect().catch(() => {});
    }
  }, [call?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Derived billing values (client-side smooth ticker) ────────────────
  const rate = call?.rate_credits_per_min || 0;
  const hold = call?.hold_credits || 0;
  const maxSecs = (call?.max_minutes || 0) * 60;
  let elapsed = 0;
  if (call?.state === 'ACTIVE') {
    const sync = serverSyncRef.current;
    elapsed = sync.elapsed + (Date.now() - sync.at) / 1000;
  }
  const remainingSecs = Math.max(maxSecs - elapsed, 0);
  const consumed = Math.min(Math.ceil(Math.max(elapsed, 1) / 60) * rate, hold);
  const walletAvailable = Math.max(hold - consumed, 0);

  // Auto-end at 0 remaining (only the caller fires it to avoid the double call).
  useEffect(() => {
    if (call?.state === 'ACTIVE' && remainingSecs <= 0 && !autoEndFired.current && !iAmCreator) {
      autoEndFired.current = true;
      endCall(callId).then(setCall).catch(() => refresh());
    }
  }, [remainingSecs, call?.state]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Actions ───────────────────────────────────────────────────────────
  const onCancelRinging = async () => {
    try { await cancelCall(callId); } catch (_e) { /* ignore */ }
    navigate(-1);
  };

  const onHangup = async () => {
    try {
      const c = await endCall(callId);
      setCall(c);
    } catch (_e) { refresh(); }
  };

  const toggleMic = async () => {
    const t = localTracksRef.current.audio;
    if (!t) return;
    if (micOn) await t.mute(); else await t.unmute();
    setMicOn(!micOn);
  };

  const toggleCam = async () => {
    const t = localTracksRef.current.video;
    if (!t) return;
    if (camOn) await t.mute(); else await t.unmute();
    setCamOn(!camOn);
  };

  // ── RENDER ────────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-neutral-950 px-6 text-center text-white" data-testid="call-error">
        <AlertTriangle className="h-9 w-9 text-red-400" />
        <p className="text-lg font-semibold">Errore chiamata</p>
        <p className="max-w-md text-sm text-neutral-400">{error}</p>
        <Button variant="outline" onClick={() => navigate('/')} data-testid="call-error-home-btn">Torna alla home</Button>
      </div>
    );
  }

  if (!call) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-950">
        <Loader2 className="h-8 w-8 animate-spin text-neutral-500" />
      </div>
    );
  }

  // Ringing (caller side).
  if (call.state === 'RINGING') {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-5 bg-neutral-950 px-6 text-white" data-testid="call-ringing">
        <div className="relative">
          <span className="absolute inset-0 animate-ping rounded-full bg-emerald-500/25" />
          <Avatar src={other?.avatar_url} alt={other?.username} size={110} />
        </div>
        <div className="text-center">
          <p className="text-xl font-semibold">@{other?.username}</p>
          <p className="mt-1 flex items-center justify-center gap-2 text-sm text-neutral-400">
            <Phone className="h-4 w-4 animate-pulse text-emerald-400" /> In attesa di risposta…
          </p>
          <p className="mt-2 text-xs text-neutral-500">
            {rate} crediti/min · max {call.max_minutes} min · {hold} crediti bloccati (paghi solo i minuti effettivi)
          </p>
        </div>
        {!iAmCreator && (
          <Button onClick={onCancelRinging} className="h-12 rounded-full bg-red-600 px-8 text-white hover:bg-red-700" data-testid="call-cancel-btn">
            <PhoneOff className="mr-2 h-4 w-4" /> Annulla
          </Button>
        )}
      </div>
    );
  }

  // Terminal states.
  if (call.state !== 'ACTIVE') {
    const msg = {
      COMPLETED: 'Chiamata terminata',
      REJECTED: 'Il creator ha rifiutato la chiamata.',
      TIMEOUT: 'Il creator non è al momento disponibile. Riprova più tardi.',
      CANCELLED: 'Chiamata annullata.',
    }[call.state] || 'Chiamata terminata';
    const released = Math.max((call.hold_credits || 0) - (call.billed_credits || 0), 0);
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-neutral-950 px-6 text-center text-white" data-testid="call-ended">
        <Avatar src={other?.avatar_url} alt={other?.username} size={80} />
        <p className="text-xl font-semibold" data-testid="call-ended-title">{msg}</p>
        {call.state === 'COMPLETED' && (
          <div className="w-full max-w-sm rounded-2xl border border-neutral-800 bg-neutral-900 px-6 py-5 text-left text-sm" data-testid="call-summary">
            <p className="mb-3 text-center text-xs font-semibold uppercase tracking-wider text-neutral-500">
              Riepilogo chiamata
            </p>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-neutral-400"><Clock className="h-4 w-4" /> Durata effettiva</span>
                <span className="font-semibold tabular-nums" data-testid="call-summary-duration">{fmtClock(call.duration_seconds || 0)}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-neutral-400">Minuti fatturati</span>
                <span className="font-semibold tabular-nums" data-testid="call-summary-billed-minutes">{call.billed_minutes || 0} min</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-neutral-400">Tariffa</span>
                <span className="font-semibold tabular-nums" data-testid="call-summary-rate">{rate} crediti/min</span>
              </div>
              <div className="my-2 border-t border-neutral-800" />
              {iAmCreator ? (
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-1.5 text-neutral-400"><Coins className="h-4 w-4 gold-text" /> Crediti guadagnati (netto)</span>
                  <span className="font-bold tabular-nums gold-text" data-testid="call-summary-earned">{call.creator_net_credits || 0}</span>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-neutral-400"><Coins className="h-4 w-4 gold-text" /> Crediti consumati</span>
                    <span className="font-bold tabular-nums gold-text" data-testid="call-summary-consumed">{call.billed_credits || 0}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-neutral-400">Crediti sbloccati</span>
                    <span className="font-semibold tabular-nums text-emerald-400" data-testid="call-summary-released">{released}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
        {(call.state === 'REJECTED' || call.state === 'TIMEOUT' || call.state === 'CANCELLED') && !iAmCreator && (
          <p className="text-xs text-emerald-400" data-testid="call-hold-released-note">
            Tutti i crediti bloccati sono stati sbloccati.
          </p>
        )}
        <Button variant="outline" onClick={() => navigate(other?.username ? `/u/${other.username}` : '/')} data-testid="call-ended-back-btn">
          Chiudi
        </Button>
      </div>
    );
  }

  // ACTIVE call room.
  const showLastMinuteWarn = remainingSecs <= 60 && remainingSecs > 30;
  const showCountdown = remainingSecs <= 30;
  const isVideoMode = (call.mode || 'video') === 'video';

  return (
    <div className="relative flex min-h-screen flex-col bg-neutral-950 text-white" data-testid="call-room">
      {/* Stats bar */}
      <div className="absolute left-0 right-0 top-0 z-20 flex flex-wrap items-center justify-center gap-2 p-3">
        <StatChip icon={Clock} label="" value={fmtClock(elapsed)} testId="call-timer" />
        <StatChip icon={Coins} label="consumati" value={`${consumed} cr`} testId="call-consumed" />
        <StatChip icon={Coins} label="disponibili" value={`${walletAvailable} cr`} testId="call-available" />
        <StatChip
          icon={Hourglass}
          label="rimanenti"
          value={fmtClock(remainingSecs)}
          warn={remainingSecs <= 60}
          testId="call-remaining"
        />
        <div className="flex items-center gap-1.5 rounded-full bg-black/50 px-3 py-1 text-xs font-semibold backdrop-blur" data-testid="call-quality">
          <QualityIcon quality={quality} />
          <span className="capitalize">{quality === 'excellent' ? 'ottima' : quality === 'good' ? 'buona' : 'scarsa'}</span>
        </div>
      </div>

      {/* Audio blocked by the browser → explicit unlock tap */}
      {audioBlocked && connState === 'connected' && (
        <div className="absolute left-1/2 top-16 z-30 -translate-x-1/2" data-testid="call-audio-blocked-banner">
          <button
            type="button"
            onClick={enableAudio}
            className="flex h-11 items-center gap-2 rounded-full bg-amber-400 px-5 text-sm font-bold text-black shadow-lg transition-transform hover:scale-[1.03]"
            data-testid="call-enable-audio-btn"
          >
            <Volume2 className="h-5 w-5" /> Tocca per attivare l'audio
          </button>
        </div>
      )}

      {/* Warning banner — 1 minute left */}
      {showLastMinuteWarn && (
        <div className="absolute left-1/2 top-16 z-30 -translate-x-1/2 rounded-full bg-amber-500 px-4 py-1.5 text-xs font-bold text-black shadow-lg" data-testid="call-warning-1min">
          ⚠️ Meno di 1 minuto rimanente
        </div>
      )}
      {/* Countdown — 30 seconds left */}
      {showCountdown && (
        <div className="absolute left-1/2 top-16 z-30 -translate-x-1/2 rounded-2xl bg-red-600 px-6 py-2 text-center shadow-xl" data-testid="call-countdown-30s">
          <p className="text-[10px] font-semibold uppercase tracking-wider">La chiamata termina tra</p>
          <p className="text-2xl font-black tabular-nums">{Math.ceil(remainingSecs)}s</p>
        </div>
      )}

      {/* Video area */}
      <div className="flex flex-1 items-center justify-center">
        {connState !== 'connected' && (
          <div className="flex flex-col items-center gap-3 text-neutral-400" data-testid="call-connecting">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">Connessione in corso…</p>
          </div>
        )}
        <div className={`relative h-screen w-full ${connState === 'connected' ? '' : 'hidden'}`}>
          {isVideoMode ? (
            <video ref={remoteVideoRef} autoPlay playsInline className="h-full w-full object-cover" data-testid="call-remote-video" />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-4">
              <Avatar src={other?.avatar_url} alt={other?.username} size={130} />
              <p className="text-lg font-semibold">@{other?.username}</p>
              <p className="text-xs text-neutral-400">Chiamata audio in corso</p>
              <video ref={remoteVideoRef} className="hidden" />
            </div>
          )}
          <audio ref={remoteAudioRef} autoPlay />
          {isVideoMode && (
            <div className="absolute bottom-24 right-4 z-20 h-40 w-28 overflow-hidden rounded-xl border border-neutral-700 bg-black shadow-xl md:h-48 md:w-36">
              <video ref={localVideoRef} autoPlay playsInline muted className="h-full w-full object-cover" data-testid="call-local-video" />
            </div>
          )}
          <div className="absolute bottom-24 left-4 z-20 rounded-full bg-black/50 px-3 py-1 text-xs backdrop-blur">
            @{other?.username || '…'}
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="absolute bottom-0 left-0 right-0 z-20 flex items-center justify-center gap-4 p-5">
        <Button
          variant="outline"
          onClick={toggleMic}
          className="h-12 w-12 rounded-full border-neutral-700 bg-neutral-900/80 p-0 text-white hover:bg-neutral-800"
          data-testid="call-mic-btn"
        >
          {micOn ? <Mic className="h-5 w-5" /> : <MicOff className="h-5 w-5 text-red-400" />}
        </Button>
        {isVideoMode && (
          <Button
            variant="outline"
            onClick={toggleCam}
            className="h-12 w-12 rounded-full border-neutral-700 bg-neutral-900/80 p-0 text-white hover:bg-neutral-800"
            data-testid="call-cam-btn"
          >
            {camOn ? <Video className="h-5 w-5" /> : <VideoOff className="h-5 w-5 text-red-400" />}
          </Button>
        )}
        <Button
          onClick={onHangup}
          className="h-12 rounded-full bg-red-600 px-8 text-white hover:bg-red-700"
          data-testid="call-hangup-btn"
        >
          <PhoneOff className="mr-2 h-5 w-5" /> Termina
        </Button>
      </div>
    </div>
  );
}
